// FILE: src/components/BadgeManager.js
//
// Admin tool — manage the home-page badges. Two areas:
//   1. Certification emblems (migration 027): create free-form emblem badges,
//      upload art, and assign one per cert (or "No emblem").
//   2. Platform feature badges (migration 029): the hero carousel — create,
//      upload art, edit label/colour, reorder, and enable/disable. Global
//      ordered set (not per-cert).
//
// Reads are public; writes hit admin-gated SECURITY DEFINER RPCs, so a non-admin
// token is rejected server-side even if the UI is reached. Each badge renders
// via the live <BadgeCallout> so previews match the landing page. No Redux.

import React, { useCallback, useEffect, useState } from 'react';
import { getAllExams } from '../../../config/exams';
import BadgeCallout from '../BadgeCallout';
import {
  listEmblemBadges,
  createEmblemBadge,
  deleteEmblemBadge,
  setCertEmblem,
  uploadBadgeAsset,
  getCertEmblems,
  listPlatformBadges,
  createPlatformBadge,
  updatePlatformBadge,
  deletePlatformBadge,
  UploadValidationError,
} from '../../../services/badgeAdminService';

const COLOR_TOKENS = ['product', 'platform', 'telemetry', 'neutral'];

const s = {
  wrap: { fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column' },
  header: { background: '#1a1a2e', color: '#fff', padding: '16px 28px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 },
  back: { padding: '6px 14px', background: 'transparent', border: '1px solid #555', borderRadius: 4, color: '#ccc', cursor: 'pointer', fontSize: '0.85rem' },
  title: { fontSize: '1.15rem', fontWeight: 'bold', flex: 1, letterSpacing: '0.5px' },
  body: { flex: 1, padding: '32px', maxWidth: 880, margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  section: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 24, marginBottom: 24 },
  h2: { fontSize: '1rem', fontWeight: 700, color: '#333', margin: '0 0 16px' },
  row: { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
  certName: { width: 130, fontWeight: 600, color: '#222' },
  select: { padding: '7px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem', minWidth: 220 },
  previewBox: { flex: 1, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
  label: { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: 4, fontWeight: 600 },
  input: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 160px', gap: 14, alignItems: 'end', marginBottom: 14 },
  btn: { padding: '8px 16px', borderRadius: 6, border: '1px solid #1a1a2e', background: '#1a1a2e', color: '#fff', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 },
  btnGhost: { padding: '6px 12px', borderRadius: 6, border: '1px solid #c0392b', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem' },
  btnSmall: { padding: '4px 9px', borderRadius: 6, border: '1px solid #bbb', background: 'transparent', color: '#333', cursor: 'pointer', fontSize: '0.8rem' },
  libRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
  swatch: (token) => ({ width: 12, height: 12, borderRadius: '50%', flex: '0 0 auto', background: token === 'platform' ? '#85b7eb' : token === 'telemetry' ? '#ef9f27' : token === 'neutral' ? '#c9d3e2' : '#8ed7a6' }),
  err: { background: '#fdecea', color: '#b71c1c', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: '0.85rem' },
  note: { fontSize: '0.8rem', color: '#888', marginTop: 6 },
  orderCol: { display: 'flex', flexDirection: 'column', gap: 2 },
};

function toEmblemCallout(badge) {
  if (!badge) return null;
  return { id: badge.id, label: badge.label, type: 'product', variant: badge.color_token || 'product', alt: badge.label, imageSrc: badge.asset_url || undefined };
}
function toPlatformCallout(b) {
  if (!b) return null;
  return { id: b.id, label: b.label, type: 'platform', variant: b.color_token || 'platform', alt: b.label, imageSrc: b.asset_url || undefined };
}
const slug = (str) => str.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');

export default function BadgeManager({ onBack }) {
  const exams = getAllExams();
  const [badges, setBadges] = useState([]);
  const [assignments, setAssignments] = useState({}); // certId -> badgeId ('' = none)
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Emblem create form
  const [label, setLabel] = useState('');
  const [colorToken, setColorToken] = useState('product');
  const [file, setFile] = useState(null);

  // Platform create form
  const [pLabel, setPLabel] = useState('');
  const [pColor, setPColor] = useState('platform');
  const [pFile, setPFile] = useState(null);

  // Inline platform edit
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('platform');

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, emblems, plats] = await Promise.all([
        listEmblemBadges(),
        getCertEmblems(),
        listPlatformBadges(),
      ]);
      setBadges(list);
      const map = {};
      Object.keys(emblems || {}).forEach((cert) => {
        map[cert] = emblems[cert] ? emblems[cert].id : '';
      });
      setAssignments(map);
      setPlatforms(plats);
    } catch (e) {
      setError(e.message || 'Failed to load badge config.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(e instanceof UploadValidationError ? e.message : e.message || 'Operation failed.');
    } finally {
      setBusy(false);
    }
  };

  // ── Emblem handlers ──────────────────────────────────────────────────────
  const handleCreate = () => {
    if (!label.trim()) { setError('A label is required to create a badge.'); return; }
    return run(async () => {
      let assetPath = null, assetUrl = null;
      if (file) {
        const up = await uploadBadgeAsset(file, { key: slug(label) });
        assetUrl = up.publicUrl; assetPath = up.path;
      }
      await createEmblemBadge({ label: label.trim(), colorToken, assetPath, assetUrl });
      setLabel(''); setColorToken('product'); setFile(null);
    });
  };
  const handleDelete = (id) => run(() => deleteEmblemBadge(id));
  const handleAssign = (certId, badgeId) => {
    setAssignments((prev) => ({ ...prev, [certId]: badgeId }));
    return run(() => setCertEmblem(certId, badgeId || null));
  };
  const badgeById = (id) => badges.find((b) => b.id === id) || null;

  // ── Platform handlers ──────────────────────────────────────────────────────
  const handlePlatformCreate = () => {
    if (!pLabel.trim()) { setError('A label is required to create a platform badge.'); return; }
    return run(async () => {
      let assetPath = null, assetUrl = null;
      if (pFile) {
        const up = await uploadBadgeAsset(pFile, { key: slug(pLabel) });
        assetUrl = up.publicUrl; assetPath = up.path;
      }
      await createPlatformBadge({ label: pLabel.trim(), colorToken: pColor, assetPath, assetUrl });
      setPLabel(''); setPColor('platform'); setPFile(null);
    });
  };
  const handlePlatformDelete = (id) => run(() => deletePlatformBadge(id));
  const handlePlatformToggle = (b) => run(() => updatePlatformBadge(b.id, { active: !b.active }));
  const handlePlatformMove = (index, dir) => {
    const j = dir === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= platforms.length) return undefined;
    const a = platforms[index], b = platforms[j];
    return run(async () => {
      await updatePlatformBadge(a.id, { sortOrder: b.sort_order });
      await updatePlatformBadge(b.id, { sortOrder: a.sort_order });
    });
  };
  const startEdit = (b) => { setEditId(b.id); setEditLabel(b.label); setEditColor(b.color_token || 'platform'); };
  const cancelEdit = () => { setEditId(null); setEditLabel(''); };
  const saveEdit = (id) => run(async () => {
    await updatePlatformBadge(id, { label: editLabel.trim(), colorToken: editColor });
    cancelEdit();
  });

  return (
    <div data-testid="badge-manager" style={s.wrap}>
      <div style={s.header}>
        <button data-testid="badge-manager-back" style={s.back} onClick={onBack}>← Back</button>
        <span style={s.title}>🏷️ Badge Manager</span>
      </div>

      <div style={s.body}>
        {error && <div data-testid="badge-manager-error" style={s.err}>{error}</div>}

        {loading ? (
          <p>Loading badge configuration…</p>
        ) : (
          <>
            {/* ── Certification emblems ───────────────────────────────────── */}
            <div style={s.section}>
              <h2 style={s.h2}>Certification emblems</h2>
              {exams.map((exam) => {
                const selectedId = assignments[exam.examId] || '';
                const previewBadge = toEmblemCallout(badgeById(selectedId));
                return (
                  <div key={exam.examId} style={s.row}>
                    <span style={s.certName}>{exam.displayName}</span>
                    <select
                      data-testid={`assign-${exam.examId}`}
                      style={s.select}
                      value={selectedId}
                      disabled={busy}
                      onChange={(e) => handleAssign(exam.examId, e.target.value)}
                    >
                      <option value="">No emblem</option>
                      {badges.map((b) => (<option key={b.id} value={b.id}>{b.label}</option>))}
                    </select>
                    <span style={s.previewBox}>
                      {previewBadge && <BadgeCallout badge={previewBadge} />}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* ── Emblem library ──────────────────────────────────────────── */}
            <div style={s.section}>
              <h2 style={s.h2}>Emblem library</h2>
              <div style={s.formGrid}>
                <div>
                  <label style={s.label} htmlFor="bm-label">Label</label>
                  <input id="bm-label" data-testid="badge-label-input" style={s.input} value={label} maxLength={28} placeholder="e.g. PMBOK 8 Ready" onChange={(e) => setLabel(e.target.value)} />
                </div>
                <div>
                  <label style={s.label} htmlFor="bm-color">Category colour</label>
                  <select id="bm-color" data-testid="badge-color-select" style={{ ...s.select, minWidth: 0, width: '100%' }} value={colorToken} onChange={(e) => setColorToken(e.target.value)}>
                    {COLOR_TOKENS.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={s.label} htmlFor="bm-file">Art (optional — PNG, JPG, WebP, or SVG, max 5 MB)</label>
                <input id="bm-file" data-testid="badge-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => setFile(e.target.files && e.target.files[0])} />
                <p style={s.note}>No art? The badge renders as a coloured text capsule using the label.</p>
              </div>
              <button data-testid="badge-create-button" style={s.btn} disabled={busy} onClick={handleCreate}>{busy ? 'Working…' : 'Create emblem'}</button>
              <div style={{ marginTop: 20 }}>
                {badges.length === 0 ? (
                  <p style={s.note}>No emblems yet. Create one above.</p>
                ) : badges.map((b) => (
                  <div key={b.id} style={s.libRow} data-testid={`badge-row-${b.id}`}>
                    <span style={s.swatch(b.color_token)} />
                    <span style={{ width: 200 }}><BadgeCallout badge={toEmblemCallout(b)} /></span>
                    <span style={{ flex: 1, fontSize: '0.85rem', color: '#666' }}>{b.asset_url ? 'image' : 'text capsule'} · {b.color_token}</span>
                    <button data-testid={`badge-delete-${b.id}`} style={s.btnGhost} disabled={busy} onClick={() => handleDelete(b.id)}>Delete</button>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Platform feature badges (carousel) ──────────────────────── */}
            <div style={s.section}>
              <h2 style={s.h2}>Platform feature badges</h2>
              <p style={s.note}>The rotating row of feature highlights in the hero. Ordered top-to-bottom; disabled badges are hidden from the carousel.</p>

              <div style={{ ...s.formGrid, marginTop: 16 }}>
                <div>
                  <label style={s.label} htmlFor="pf-label">Label</label>
                  <input id="pf-label" data-testid="platform-label-input" style={s.input} value={pLabel} maxLength={28} placeholder="e.g. Customized Study Guide" onChange={(e) => setPLabel(e.target.value)} />
                </div>
                <div>
                  <label style={s.label} htmlFor="pf-color">Category colour</label>
                  <select id="pf-color" style={{ ...s.select, minWidth: 0, width: '100%' }} value={pColor} onChange={(e) => setPColor(e.target.value)}>
                    {COLOR_TOKENS.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={s.label} htmlFor="pf-file">Art (optional — PNG, JPG, WebP, or SVG, max 5 MB)</label>
                <input id="pf-file" data-testid="platform-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => setPFile(e.target.files && e.target.files[0])} />
              </div>
              <button data-testid="platform-create-button" style={s.btn} disabled={busy} onClick={handlePlatformCreate}>{busy ? 'Working…' : 'Create feature badge'}</button>

              <div style={{ marginTop: 20 }}>
                {platforms.length === 0 ? (
                  <p style={s.note}>No platform badges yet. Create one above.</p>
                ) : platforms.map((b, i) => (
                  <div key={b.id} style={s.libRow} data-testid={`platform-row-${b.id}`}>
                    <span style={s.orderCol}>
                      <button style={s.btnSmall} disabled={busy || i === 0} title="Move up" onClick={() => handlePlatformMove(i, 'up')}>▲</button>
                      <button style={s.btnSmall} disabled={busy || i === platforms.length - 1} title="Move down" onClick={() => handlePlatformMove(i, 'down')}>▼</button>
                    </span>
                    {editId === b.id ? (
                      <>
                        <input style={{ ...s.input, width: 180 }} value={editLabel} maxLength={28} onChange={(e) => setEditLabel(e.target.value)} />
                        <select style={{ ...s.select, minWidth: 0, width: 120 }} value={editColor} onChange={(e) => setEditColor(e.target.value)}>
                          {COLOR_TOKENS.map((t) => (<option key={t} value={t}>{t}</option>))}
                        </select>
                        <button style={s.btn} disabled={busy} onClick={() => saveEdit(b.id)}>Save</button>
                        <button style={s.btnSmall} disabled={busy} onClick={cancelEdit}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span style={{ width: 190, opacity: b.active ? 1 : 0.4 }}><BadgeCallout badge={toPlatformCallout(b)} /></span>
                        <label style={{ flex: 1, fontSize: '0.85rem', color: '#666', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <input type="checkbox" data-testid={`platform-active-${b.id}`} checked={!!b.active} disabled={busy} onChange={() => handlePlatformToggle(b)} />
                          {b.active ? 'shown' : 'hidden'} · {b.asset_url ? 'image' : 'text'}
                        </label>
                        <button style={s.btnSmall} disabled={busy} onClick={() => startEdit(b)}>Edit</button>
                        <button data-testid={`platform-delete-${b.id}`} style={s.btnGhost} disabled={busy} onClick={() => handlePlatformDelete(b.id)}>Delete</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
